import {
  CreateSurveySessionResponse,
  GetSurveyResponseReceipt,
  HelpCenterApiArgs,
  HelpCenterApiResponse,
  LiveChatCreateArgs,
  LiveChatCreateResponseData,
  LiveChatRefreshTokenArgs,
  LiveChatRefreshTokenResponseData,
  SurveySessionCreateArgs,
  SurveySessionUpdateArgs,
  UpdateSurveySessionResponse,
  WiseOwlIngress,
  WiseOwlUser,
  getHelpCenterApiServiceEndpoint,
  helpCenterEndpoints,
} from "@twiliointernal/e2-commons-shared-definitions";
import Client from "./client";

class HelpCenterApi extends Client {
  token: string | undefined;
  user: WiseOwlUser | undefined;

  constructor({ ingress, token, user }: HelpCenterApiArgs = {}) {
    const serviceEndpoint = getHelpCenterApiServiceEndpoint(ingress);
    super(serviceEndpoint, ingress);

    if (ingress === WiseOwlIngress.sendGrid && token && user) {
      this.token = token;
      this.user = user;
    }
  }

  createLiveChat = async (liveChatData: LiveChatCreateArgs) => {
    const response = await this.post<LiveChatCreateResponseData>({
      path: helpCenterEndpoints.createLiveChat(),
      body: JSON.stringify(liveChatData),
      requestOptions: {
        credentials: "include",
      },
    });

    return response;
  };

  refreshToken = async (body: LiveChatRefreshTokenArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions();

    if (this.ingress === WiseOwlIngress.sendGrid && this.token && this.user) {
      headers["i-sendgrid-auth-mako-token"] = this.token;
      headers["i-sendgrid-auth-user-id"] = this.user.sendGridUserId || "";
    }

    const response = await this.post<LiveChatRefreshTokenResponseData>({
      path: helpCenterEndpoints.refreshLiveChatToken(),
      headers,
      body: JSON.stringify(body),
      requestOptions,
    });

    return response as HelpCenterApiResponse<LiveChatRefreshTokenResponseData>;
  };

  endLiveChat = async (liveChatId: string) => {
    const { headers, requestOptions } = await this.getRequestOptions();

    if (this.ingress === WiseOwlIngress.sendGrid && this.token && this.user) {
      headers["i-sendgrid-auth-mako-token"] = this.token;
      headers["i-sendgrid-auth-user-id"] = this.user.sendGridUserId || "";
    }

    return this.patch({
      path: helpCenterEndpoints.endLiveChat(liveChatId),
      headers,
      body: JSON.stringify({ action: "end" }),
      requestOptions,
    });
  };

  /**
   * Create a new survey session for the given survey and variant ID and get the survey questions or initial question/s.
   * @param options - The survey ID and variant ID.
   * @returns The survey session ID and the survey questions.
   */
  createSurveySession = async ({
    surveyId,
    surveyVariant,
    headers: extraHeaders,
  }: SurveySessionCreateArgs): Promise<
    HelpCenterApiResponse<CreateSurveySessionResponse>
  > => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    if (this.ingress === WiseOwlIngress.sendGrid && this.token && this.user) {
      headers["i-sendgrid-auth-mako-token"] = this.token;
      headers["i-sendgrid-auth-user-id"] = this.user.sendGridUserId || "";
    }

    const response = await this.post<CreateSurveySessionResponse>({
      path: helpCenterEndpoints.createSurveySession(surveyId),
      body: JSON.stringify({
        surveyVariant,
      }),
      headers,
      requestOptions,
    });

    return response as HelpCenterApiResponse<CreateSurveySessionResponse>;
  };

  /**
   * Update the survey session with the embedded data and responses to the survey questions.
   * @param options - The survey session ID, survey ID, embedded data and the responses to the survey questions.
   * @returns The updated survey session, and additional questions if any.
   */
  updateSurveySession = async ({
    embeddedData,
    sessionId,
    surveyId,
    responses,
  }: SurveySessionUpdateArgs): Promise<
    HelpCenterApiResponse<UpdateSurveySessionResponse>
  > => {
    const { headers, requestOptions } = await this.getRequestOptions();

    if (this.ingress === WiseOwlIngress.sendGrid && this.token && this.user) {
      headers["i-sendgrid-auth-mako-token"] = this.token;
      headers["i-sendgrid-auth-user-id"] = this.user.sendGridUserId || "";
    }

    const response = await this.post<UpdateSurveySessionResponse>({
      path: helpCenterEndpoints.updateSurveySession(surveyId, sessionId),
      body: JSON.stringify({
        embeddedData,
        responses,
      }),
      headers,
      requestOptions,
    });

    return response as HelpCenterApiResponse<UpdateSurveySessionResponse>;
  };

  /**
   * Get a list of survey response receipts by live chat ID.
   * @param liveChatId - The live chat ID.
   * @returns List of survey response receipts.
   */
  getSurveyResponseReceiptsByLiveChatId = async (liveChatId: string) => {
    const { headers, requestOptions } = await this.getRequestOptions();

    if (this.ingress === WiseOwlIngress.sendGrid && this.token && this.user) {
      headers["i-sendgrid-auth-mako-token"] = this.token;
      headers["i-sendgrid-auth-user-id"] = this.user.sendGridUserId || "";
    }

    const response = await this.post<GetSurveyResponseReceipt>({
      path: helpCenterEndpoints.getSurveyResponseReceipts(),
      body: JSON.stringify({ liveChatId }),
      headers,
      requestOptions,
    });

    return response as HelpCenterApiResponse<GetSurveyResponseReceipt>;
  };
}

export default HelpCenterApi;
